import { Injectable, inject } from '@angular/core';
import { Router, CanActivate, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { OrganizationService } from '../services/organization.service';
import { firstValueFrom } from 'rxjs';
import { Auth, authState } from '@angular/fire/auth';

/** Planning Center's Firebase OIDC provider id. */
const PCO_PROVIDER_ID = 'oidc.planningcenteronline.com';

/**
 * Organization Guard
 * Ensures user has an organization before accessing protected routes.
 *
 * Where an org-less user goes depends on how they signed in. A Planning Center
 * user's organisation is determined by their sign-in claim, so they go to
 * /organization-setup, which reads it. A standalone user has no such claim —
 * they either create an organisation or need an invitation — so they go to
 * /auth/register instead, which explains both.
 */
@Injectable({ providedIn: 'root' })
export class OrganizationGuard implements CanActivate {
    private router = inject(Router);
    private organizationService = inject(OrganizationService);
    private auth = inject(Auth);
    private authState$ = authState(this.auth);

    /** Where an org-less user should be sent, based on their sign-in provider. */
    private setupRouteFor(user: { providerData?: { providerId: string }[] }): string {
        const usedPco = user.providerData?.some(p => p.providerId === PCO_PROVIDER_ID);
        return usedPco ? '/organization-setup' : '/auth/register';
    }

    async canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> {
        try {
            const user = await firstValueFrom(this.authState$);

            if (!user || !user.uid) {
                console.log('OrganizationGuard: No authenticated user found');
                this.router.navigate(['/auth/login'], { queryParams: { returnUrl: state.url } });
                return false;
            }

            console.log('OrganizationGuard: Checking organization for user:', user.uid);

            // If not in localStorage, check Firestore
            try {
                const userOrg = await firstValueFrom(
                    this.organizationService.getUserOrganization(user.uid)
                );

                if (!userOrg || !userOrg.organizationId) {
                    const target = this.setupRouteFor(user);
                    console.log(`OrganizationGuard: User has no organization - redirecting to ${target}`);
                    this.router.navigate([target]);
                    return false;
                }

                console.log('OrganizationGuard: ✓ User has valid organization from Firestore');

                return true;

            } catch (error: any) {
                console.error('OrganizationGuard: Error fetching user organization:', error);

                // Only redirect to setup if it's definitely a "no organization" error
                // For transient errors (network, etc.), allow access to prevent disruption
                if (error?.code === 'permission-denied') {
                    const target = this.setupRouteFor(user);
                    console.log(`OrganizationGuard: Permission denied - redirecting to ${target}`);
                    this.router.navigate([target]);
                    return false;
                }

                // For other errors, allow access (fail open for better UX)
                console.warn('OrganizationGuard: Error checking organization, allowing access to prevent disruption');
                return true;
            }

        } catch (error) {
            console.error('OrganizationGuard: Unexpected error:', error);
            // Fail open - allow access rather than blocking user
            return true;
        }
    }
}
